import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SearchIcon, ShieldCheckIcon, ClockIcon, MinusCircleIcon, CheckCircle2Icon, HashIcon, StarIcon, ChevronRightIcon } from 'lucide-react';
import { User, LicenseData, MichiganLicenseData, US_STATES, StateStatus } from './types';
interface UserLicenseModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  licenses: Record<string, LicenseData>;
  michiganLicense: MichiganLicenseData | undefined;
  onOpenState: (stateName: string) => void;
}
const STATUS_COLORS: Record<StateStatus, {
  bg: string;
  text: string;
  border: string;
}> = {
  not_started: {
    bg: 'bg-slate-800/40',
    text: 'text-slate-500',
    border: 'border-slate-700/50'
  },
  in_progress: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    border: 'border-amber-500/30'
  },
  complete: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30'
  }
};
export function UserLicenseModal({
  user,
  isOpen,
  onClose,
  licenses,
  michiganLicense,
  onOpenState
}: UserLicenseModalProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | StateStatus>('all');
  const statesWithStatus = useMemo(() => {
    return US_STATES.map(state => {
      if (state.name === 'Michigan') {
        const mi = michiganLicense;
        let status: StateStatus = 'not_started';
        if (mi) {
          if (mi.steps.every(Boolean)) status = 'complete';else if (mi.steps.some(Boolean)) status = 'in_progress';
        }
        return {
          ...state,
          status
        };
      }
      const lic = licenses[state.name];
      return {
        ...state,
        status: lic?.status || 'not_started' as StateStatus
      };
    });
  }, [licenses, michiganLicense]);
  const filteredStates = useMemo(() => {
    let result = statesWithStatus;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q) || s.abbr.toLowerCase().includes(q));
    }
    if (filter !== 'all') {
      result = result.filter(s => s.status === filter);
    }
    return result;
  }, [statesWithStatus, search, filter]);
  const counts = useMemo(() => {
    let licensed = 0;
    let inProgress = 0;
    for (const s of statesWithStatus) {
      if (s.status === 'complete') licensed++;else if (s.status === 'in_progress') inProgress++;
    }
    return {
      licensed,
      inProgress
    };
  }, [statesWithStatus]);
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-535" data-mp="3c3j6lbsr6w22">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-536" data-mp="3h1ucjbfxgmf0">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-537" data-mp="3gzro2vufb1ce" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-538" data-mp="3utae8l56j1k">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800" data-id="element-539" data-mp="8s1vagxod16i">
            <div className="flex items-center gap-3 min-w-0" data-id="element-540" data-mp="2ygu3vi8wr6rv">
              <div className="w-10 h-10 rounded-xl bg-slate-700/50 border border-slate-600/50 flex items-center justify-center text-sm font-bold text-slate-300 flex-shrink-0" data-id="element-541" data-mp="2yif0ddes8zsd">
                {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0" data-id="element-542" data-mp="ca7fjp5j4bbc">
                <h2 className="text-lg font-bold text-white truncate" data-id="element-543" data-mp="h7quysdyqu4v">
                  {user.name}
                </h2>
                <div className="flex items-center gap-1.5" data-id="element-544" data-mp="14ws9mxde31il">
                  <HashIcon className="w-3 h-3 text-slate-500" data-id="element-545" data-mp="14v7fh8uzlkl9" />
                  <span className="text-xs text-slate-500 tabular-nums" data-id="element-546" data-mp="18xlwroj0ccaf">
                    {user.nmlsId}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0" data-id="element-547" data-mp="zx5uujhwxd7o">
              <div className="flex items-center gap-2" data-id="element-548" data-mp="3fpmlae6vjyqo">
                {counts.licensed > 0 && <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-lg" data-id="element-549" data-mp="3ko6dgwc2a6rh">
                    <ShieldCheckIcon className="w-3.5 h-3.5 text-emerald-400" data-id="element-550" data-mp="3d3vr2t2zbbi2" />
                    <span className="text-xs font-bold text-emerald-400 tabular-nums" data-id="element-551" data-mp="386dgky9bco7y">
                      {counts.licensed}
                    </span>
                  </div>}
                {counts.inProgress > 0 && <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-id="element-552" data-mp="3h71vrwag0umf">
                    <ClockIcon className="w-3.5 h-3.5 text-amber-400" data-id="element-553" data-mp="2tel8n7bumcg9" />
                    <span className="text-xs font-bold text-amber-400 tabular-nums" data-id="element-554" data-mp="2pgx8npvatthi">
                      {counts.inProgress}
                    </span>
                  </div>}
              </div>
              <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-555" data-mp="2ydxuppuwbfhs">
                <XIcon className="w-5 h-5" data-id="element-556" data-mp="25r3fp8gul8bp" />
              </button>
            </div>
          </div>

          {/* Search & Filter */}
          <div className="px-6 py-3 border-b border-slate-800/50 flex flex-col sm:flex-row gap-2.5" data-id="element-557" data-mp="en1u3fhjl2ap">
            <div className="flex-1 flex items-center bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 focus-within:border-emerald-500/50 transition-all" data-id="element-558" data-mp="jea52fyisvpz">
              <SearchIcon className="w-3.5 h-3.5 text-slate-500 mr-2" data-id="element-559" data-mp="of7i6e4oazkm" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search states…" className="w-full bg-transparent text-sm text-slate-200 focus:outline-none placeholder:text-slate-500" data-id="element-560" data-mp="3mw1yzzw7vpqt" />
            </div>
            <div className="flex bg-slate-800/60 rounded-lg p-0.5 border border-slate-700/50" data-id="element-561" data-mp="4msv5tw612rm">
              <button onClick={() => setFilter('all')} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === 'all' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-562" data-mp="1c51umul2ym4c">
                All
              </button>
              <button onClick={() => setFilter('complete')} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === 'complete' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-563" data-mp="2ru1er8808zoo">
                Licensed
              </button>
              <button onClick={() => setFilter('in_progress')} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === 'in_progress' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-564" data-mp="2cvpj94wdbwz3">
                In Progress
              </button>
              <button onClick={() => setFilter('not_started')} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === 'not_started' ? 'bg-slate-600 text-slate-200' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-565" data-mp="71ptx00276fv">
                Not Started
              </button>
            </div>
          </div>

          {/* State List */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-1" data-id="element-566" data-mp="2xctwhknqt364">
            {filteredStates.map(state => {
            const colors = STATUS_COLORS[state.status];
            const isMichigan = state.name === 'Michigan';
            return <button key={state.name} onClick={() => onOpenState(state.name)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-150 hover:bg-opacity-80 group ${colors.bg} ${colors.border}`} data-id="element-567" data-mp="2sheo388i7zh3">
                  <span className={`text-sm font-bold w-8 ${colors.text}`} data-id="element-568" data-mp="cpie9kxdb1n3">
                    {state.abbr}
                  </span>
                  <span className="text-sm text-slate-300 flex-1 text-left flex items-center gap-2" data-id="element-569" data-mp="225uho1s0yx1u">
                    {state.name}
                    {isMichigan && <StarIcon className="w-3.5 h-3.5 text-amber-500 fill-amber-500" data-id="element-570" data-mp="1x4n8bya8hpt2" />}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-571" data-mp="2757d3awypcg1">
                    {state.status === 'complete' && <CheckCircle2Icon className="w-4 h-4 text-emerald-400" data-id="element-572" data-mp="2kvj5zc1dff88" />}
                    {state.status === 'in_progress' && <ClockIcon className="w-4 h-4 text-amber-400" data-id="element-573" data-mp="2fzlp94m746ux" />}
                    {state.status === 'not_started' && <MinusCircleIcon className="w-4 h-4 text-slate-600" data-id="element-574" data-mp="2pwgq3u5xyjcd" />}
                    <ChevronRightIcon className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors" data-id="element-575" data-mp="10hikzxkx8g4y" />
                  </div>
                </button>;
          })}
            {filteredStates.length === 0 && <div className="text-center py-8 text-sm text-slate-500" data-id="element-576" data-mp="1qqtzno37ixss">
                No states match your search
              </div>}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800 flex justify-end" data-id="element-577" data-mp="1guz986sqryfg">
            <button onClick={onClose} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors" data-id="element-578" data-mp="1bx79iqluv80p">
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}